import { gql } from "@apollo/client";
import { useRouter } from "next/router";
import { FC, useMemo, useState } from "react";

import useUser from "@src/features/app/hooks/useUser";
import SelectUsersDialog, {
  USER_SELECTION_FILTERS,
} from "@src/features/contentLibrary/assignmentDialogs/SelectUsersDialog";
import UserSelectionSetPreview from "@src/features/contentLibrary/assignmentDialogs/UserSelectionSetPreview";
import useUserSelectionSetInputState from "@src/features/contentLibrary/assignmentDialogs/hooks/useUserSelectionSetInputState";
import DotsIcon from "@src/lib/icons/18px/dots";
import PlusIcon from "@src/lib/icons/18px/plus";
import UserMinusOutlineIcon from "@src/lib/icons/18px/user-minus-outline";
import { useToast } from "@src/lib/hooks/useToast";
import Avatar from "@src/lib/ui/Avatar";
import { Button } from "@src/lib/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@src/lib/ui/DropdownMenu";
import { Input } from "@src/lib/ui/Input";
import { Switch } from "@src/lib/ui/Switch";
import Text from "@src/lib/ui/Text";
import { Textarea } from "@src/lib/ui/Textarea";
import {
  CreateGroupMessageInput,
  GraphqlOperations,
} from "@src/types.generated";

import { ChatDetailFragment } from "./ChatDetailPage.generated";
import DeleteChatDialog from "./DeleteChatDialog";
import {
  useCreateGroupMessageMutation,
  useGroupMessageOwnerUsersQuery,
  useUpdateGroupMessageDetailsMutation,
  useUpdateGroupMessageMemberPermissionsMutation,
  useUpdateGroupMessageMembersMutation,
  useUpdateGroupMessageOwnersMutation,
} from "./EditOrCreateGroupMessageDialog.generated";
import RemoveSelfAsOwnerDialog from "./RemoveSelfAsOwnerDialog";
import { getMemberRoleSubtitle } from "./utils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chat?: ChatDetailFragment;
  onDeleted?: () => void;
  onUpdated?: () => void;
  // Replaces the default create, which opens the new chat. Resolve true to close the dialog.
  onCreate?: (input: CreateGroupMessageInput) => Promise<boolean>;
};

const PEOPLE_ONLY_HIDE_FILTERS = USER_SELECTION_FILTERS.filter(
  (filter) => filter !== "people",
);

const CREATE_FAILED_MESSAGE = "Couldn't create the group message. Try again.";

const sameIdSet = (a: number[], b: number[]): boolean =>
  a.length === b.length && [...a].sort().join() === [...b].sort().join();

const EditOrCreateGroupMessageDialog: FC<Props> = ({
  open,
  onOpenChange,
  chat,
  onDeleted,
  onUpdated,
  onCreate,
}) => {
  const router = useRouter();
  const { user } = useUser();
  const { addToast, addErrorToast } = useToast();

  const canEditTitle =
    !chat || chat.viewerIsAdminOrChatOwner || chat.membersCanEditTitle;
  const canEditMembers =
    !chat || chat.viewerIsAdminOrChatOwner || chat.membersCanEditMembers;
  const isAdminOrOwner = !chat || chat.viewerIsAdminOrChatOwner;

  const initialOwnerIds = useMemo(
    () => chat?.owners.map((owner) => owner.id) ?? (user ? [user.id] : []),
    [chat?.owners, user],
  );

  const [title, setTitle] = useState(chat?.title?.en ?? "");
  const [description, setDescription] = useState(chat?.description?.en ?? "");
  const [ownerUserIds, setOwnerUserIds] = useState(initialOwnerIds);
  const [membersCanEditTitle, setMembersCanEditTitle] = useState(
    chat?.membersCanEditTitle ?? false,
  );
  const [membersCanEditMembers, setMembersCanEditMembers] = useState(
    chat?.membersCanEditMembers ?? false,
  );
  const [membersDirty, setMembersDirty] = useState(false);
  const [editMembersOpen, setEditMembersOpen] = useState(false);
  const [addOwnerOpen, setAddOwnerOpen] = useState(false);
  const [removeSelfAsOwnerOpen, setRemoveSelfAsOwnerOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const { userSelectionSetInput, editUserSelection, addUserSelection } =
    useUserSelectionSetInputState(
      chat ? { initialUserSelectionSetFragment: chat.userSelectionSet } : {},
    );

  const chatOwnersById = useMemo(
    () => new Map(chat?.owners.map((owner) => [owner.id, owner])),
    [chat?.owners],
  );
  const newOwnerIds = ownerUserIds.filter((id) => !chatOwnersById.has(id));

  const { data: ownersData, previousData: previousOwnersData } =
    useGroupMessageOwnerUsersQuery({
      variables: {
        input: {
          allLocations: false,
          allManagers: false,
          allTrainees: false,
          allAdmins: false,
          userIds: newOwnerIds,
        },
      },
      skip: newOwnerIds.length === 0,
    });
  const newOwnersById = new Map(
    (
      ownersData?.GetUsersForUserSelection ??
      previousOwnersData?.GetUsersForUserSelection ??
      []
    ).map((owner) => [owner.id, owner]),
  );
  const owners = ownerUserIds
    .map((id) => chatOwnersById.get(id) ?? newOwnersById.get(id))
    .filter((owner) => owner != null);

  const refetchQueries = [GraphqlOperations.Query.NewMessagingChatSections];
  const [createGroupMessage] = useCreateGroupMessageMutation({
    refetchQueries,
  });
  const [updateGroupMessageDetails] = useUpdateGroupMessageDetailsMutation({
    refetchQueries,
  });
  const [updateGroupMessageMembers] = useUpdateGroupMessageMembersMutation({
    refetchQueries,
  });
  const [updateGroupMessageOwners] = useUpdateGroupMessageOwnersMutation({
    refetchQueries,
  });
  const [updateGroupMessageMemberPermissions] =
    useUpdateGroupMessageMemberPermissionsMutation({ refetchQueries });

  const trimmedTitle = title.trim();
  const trimmedDescription = description.trim();
  const titleChanged = trimmedTitle !== (chat?.title?.en ?? "");
  const descriptionChanged =
    trimmedDescription !== (chat?.description?.en ?? "");
  const ownersChanged = !sameIdSet(ownerUserIds, initialOwnerIds);

  const removeOwner = (ownerId: number) =>
    setOwnerUserIds((ids) => ids.filter((id) => id !== ownerId));

  const handleCreate = async () => {
    setSaving(true);
    const input: CreateGroupMessageInput = {
      userSelectionSet: userSelectionSetInput,
      ...(trimmedTitle && { title: { en: trimmedTitle } }),
      ...(trimmedDescription && {
        description: { en: trimmedDescription },
      }),
      ownerUserIds,
      membersCanEditTitle,
      membersCanEditMembers,
    };
    if (onCreate) {
      try {
        if (await onCreate(input)) onOpenChange(false);
      } finally {
        setSaving(false);
      }
      return;
    }
    try {
      const res = await createGroupMessage({ variables: { input } });
      if (
        !res.data?.createGroupMessage.success ||
        !res.data.createGroupMessage.chat
      ) {
        addErrorToast({
          data: res.data,
          message: CREATE_FAILED_MESSAGE,
          callsite: "create_group_message",
        });
        return;
      }
      onOpenChange(false);
      router.push({
        pathname: "/messages/my-chats/[chatId]",
        query: { chatId: res.data.createGroupMessage.chat.id },
      });
    } catch (error) {
      addErrorToast({
        error,
        message: CREATE_FAILED_MESSAGE,
        callsite: "create_group_message",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!chat) {
      await handleCreate();
      return;
    }
    const permissionsChanged =
      membersCanEditTitle !== chat.membersCanEditTitle ||
      membersCanEditMembers !== chat.membersCanEditMembers;
    setSaving(true);
    try {
      if (titleChanged || descriptionChanged) {
        const res = await updateGroupMessageDetails({
          variables: {
            input: {
              chatId: chat.id,
              ...(titleChanged && { title: { en: trimmedTitle } }),
              // A blank `en` is how the server clears the description.
              ...(descriptionChanged && {
                description: { en: trimmedDescription },
              }),
            },
          },
        });
        if (!res.data?.updateChat.success) {
          addErrorToast({ data: res.data, callsite: "edit_group_message" });
          return;
        }
      }
      if (isAdminOrOwner && permissionsChanged) {
        const res = await updateGroupMessageMemberPermissions({
          variables: {
            input: {
              chatId: chat.id,
              membersCanEditTitle,
              membersCanEditMembers,
            },
          },
        });
        if (!res.data?.updateChatMemberPermissions.success) {
          addErrorToast({ data: res.data, callsite: "edit_group_message" });
          return;
        }
      }
      let leftChat = false;
      if (membersDirty) {
        const res = await updateGroupMessageMembers({
          variables: {
            input: { chatId: chat.id, userSelectionSet: userSelectionSetInput },
          },
        });
        if (!res.data?.updateChatMembers.success) {
          addErrorToast({ data: res.data, callsite: "edit_group_message" });
          return;
        }
        leftChat = !res.data.updateChatMembers.chat;
      }
      if (isAdminOrOwner && ownersChanged) {
        const res = await updateGroupMessageOwners({
          variables: { input: { chatId: chat.id, ownerUserIds } },
        });
        if (!res.data?.updateChatOwners.success) {
          addErrorToast({ data: res.data, callsite: "edit_group_message" });
          return;
        }
      }
      onOpenChange(false);
      if (
        titleChanged ||
        descriptionChanged ||
        (isAdminOrOwner && (permissionsChanged || ownersChanged)) ||
        membersDirty
      ) {
        onUpdated?.();
      }
      if (leftChat) {
        addToast({ message: "You left the group message" });
        router.push("/messages");
      } else {
        addToast({ message: "Group message updated" });
      }
    } catch (error) {
      addErrorToast({ error, callsite: "edit_group_message" });
    } finally {
      setSaving(false);
    }
  };

  const handleMembersSelected = (
    selected: Parameters<typeof editUserSelection>[1],
  ) => {
    if (userSelectionSetInput.userSelections.length === 0) {
      addUserSelection(selected);
    } else {
      editUserSelection(0, selected);
    }
    setMembersDirty(true);
    setEditMembersOpen(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!saving) onOpenChange(next);
        }}
      >
        <DialogContent className="flex max-h-[90vh] max-w-[560px] flex-col gap-0 p-0">
          <DialogHeader className="items-center border-b border-input px-5 py-3">
            <DialogTitle asChild>
              <Text variant="compact-title">
                {chat ? "Edit group message" : "New group message"}
              </Text>
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-1 flex-col gap-6 overflow-auto p-5">
            <div className="flex flex-col gap-2">
              <Text variant="compact-title">Custom title</Text>
              <Input
                value={title}
                placeholder="Title"
                disabled={!canEditTitle}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Text variant="compact-title">Description</Text>
              <Textarea
                value={description}
                placeholder="Description"
                aria-label="Description"
                autoSize
                minRows={2}
                maxRows={6}
                disabled={!isAdminOrOwner}
                onChange={(e) => setDescription(e.target.value)}
                className="rounded-lg"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Text variant="compact-title">Members</Text>
              <UserSelectionSetPreview
                input={userSelectionSetInput}
                chatId={chat?.id}
                showUserTypeOptions
                disabled={!canEditMembers}
                showAdd={false}
                showRemove={false}
                onEdit={
                  canEditMembers ? () => setEditMembersOpen(true) : undefined
                }
                onAdd={
                  canEditMembers ? () => setEditMembersOpen(true) : undefined
                }
              />
            </div>
            {isAdminOrOwner && (
              <div className="flex flex-col gap-4">
                <Text variant="compact-title">Owners</Text>
                <div className="flex flex-col gap-3">
                  {owners.map((owner) => (
                    <div key={owner.id} className="flex items-center gap-2.5">
                      <Avatar person={owner} size={40} />
                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <Text variant="compact-title" ellipsis>
                          {owner.name}
                        </Text>
                        <Text
                          variant="fine-print-description"
                          className="text-gray-8"
                          ellipsis
                        >
                          {getMemberRoleSubtitle(owner)}
                        </Text>
                      </div>
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Owner options for ${owner.name}`}
                          >
                            <DotsIcon />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="text-red-9 focus:text-red-9"
                            disabled={ownerUserIds.length <= 1}
                            onSelect={() =>
                              owner.id === user?.id
                                ? setRemoveSelfAsOwnerOpen(true)
                                : removeOwner(owner.id)
                            }
                          >
                            <UserMinusOutlineIcon className="size-4" />
                            Remove owner
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))}
                </div>
                <div className="flex">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAddOwnerOpen(true)}
                  >
                    <PlusIcon className="size-4" />
                    Add an owner
                  </Button>
                </div>
              </div>
            )}
            {isAdminOrOwner && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <Text variant="compact-title">Permissions</Text>
                  <Text
                    variant="fine-print-description"
                    className="text-gray-9"
                  >
                    Turning off these permissions means only owners can take
                    these actions
                  </Text>
                </div>
                <PermissionToggle
                  label="Members can edit title"
                  checked={membersCanEditTitle}
                  onCheckedChange={setMembersCanEditTitle}
                />
                <PermissionToggle
                  label="Members can add and remove members"
                  checked={membersCanEditMembers}
                  onCheckedChange={setMembersCanEditMembers}
                />
              </div>
            )}
          </div>
          <DialogFooter className="border-t border-input p-3 sm:justify-between">
            {chat && isAdminOrOwner ? (
              <Button
                variant="outline"
                className="text-status-error-text hover:bg-status-error-bg hover:text-status-error-text"
                onClick={() => setDeleteOpen(true)}
                disabled={saving}
              >
                Delete
              </Button>
            ) : (
              <div />
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                loading={saving}
                loadingText="Saving…"
                disabled={
                  ownerUserIds.length === 0 ||
                  userSelectionSetInput.userSelections.length === 0
                }
              >
                Save
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
        {removeSelfAsOwnerOpen && (
          <RemoveSelfAsOwnerDialog
            open={removeSelfAsOwnerOpen}
            onOpenChange={setRemoveSelfAsOwnerOpen}
            onConfirm={() => {
              if (user) {
                removeOwner(user.id);
              }
              setRemoveSelfAsOwnerOpen(false);
            }}
          />
        )}
        {deleteOpen && chat && (
          <DeleteChatDialog
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            chatId={chat.id}
            onDeleted={() => {
              onOpenChange(false);
              onDeleted?.();
            }}
          />
        )}
      </Dialog>
      {editMembersOpen && (
        <SelectUsersDialog
          open={editMembersOpen}
          onOpenChange={setEditMembersOpen}
          title="Edit members"
          pillColor="purple"
          requireSelection
          showUserTypeOptions
          defaultFilter="people"
          input={userSelectionSetInput.userSelections[0]}
          onSelect={handleMembersSelected}
        />
      )}
      {addOwnerOpen && (
        <SelectUsersDialog
          open={addOwnerOpen}
          onOpenChange={setAddOwnerOpen}
          title="Add an owner"
          pillColor="purple"
          requireSelection
          hideFilters={PEOPLE_ONLY_HIDE_FILTERS}
          onSelect={(selected) => {
            const addedIds = selected.userIds ?? [];
            setOwnerUserIds((ids) =>
              Array.from(new Set([...ids, ...addedIds])),
            );
            setAddOwnerOpen(false);
          }}
        />
      )}
    </>
  );
};

const PermissionToggle: FC<{
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}> = ({ label, checked, onCheckedChange }) => (
  <label className="flex items-center justify-between gap-3">
    <Text variant="compact-title">{label}</Text>
    <Switch checked={checked} onCheckedChange={onCheckedChange} />
  </label>
);

gql`
  query GroupMessageOwnerUsers($input: UserSelectionInput!) {
    GetUsersForUserSelection(input: $input) {
      id
      name
      firstName
      deactivatedAt
      userType
      profileImage {
        ...AvatarProfileImage
      }
      roles {
        id
        name
      }
      locations {
        id
        name
      }
    }
  }

  mutation CreateGroupMessage($input: CreateGroupMessageInput!) {
    createGroupMessage(input: $input) {
      success
      error {
        code
      }
      chat {
        ...ChatListItem
      }
    }
  }

  mutation UpdateGroupMessageDetails($input: UpdateChatInput!) {
    updateChat(input: $input) {
      success
      error {
        code
      }
      chat {
        ...ChatDetail
      }
    }
  }

  mutation UpdateGroupMessageMembers($input: UpdateChatMembersInput!) {
    updateChatMembers(input: $input) {
      success
      error {
        code
      }
      chat {
        ...ChatDetail
      }
    }
  }

  mutation UpdateGroupMessageOwners($input: UpdateChatOwnersInput!) {
    updateChatOwners(input: $input) {
      success
      error {
        code
      }
      chat {
        ...ChatDetail
      }
    }
  }

  mutation UpdateGroupMessageMemberPermissions(
    $input: UpdateChatMemberPermissionsInput!
  ) {
    updateChatMemberPermissions(input: $input) {
      success
      error {
        code
      }
      chat {
        ...ChatDetail
      }
    }
  }
`;

export default EditOrCreateGroupMessageDialog;
